import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, CheckCircle2 } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { projectsApi, serpApi, type Project } from "@/api";
import { MultiSelectFilter, type MultiSelectOption } from "@/components/MultiSelectFilter";
import { LOCATION_OPTIONS, LANGUAGE_OPTIONS } from "@/lib/serp-constants";

export default function BatchAnalysis() {
  const [keywords, setKeywords] = useState("");
  const [projectName, setProjectName] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [createNewProject, setCreateNewProject] = useState(false);
  const [locationCodes, setLocationCodes] = useState<Set<string>>(new Set(["2158"]));
  const [languageCodes, setLanguageCodes] = useState<Set<string>>(new Set(["zh-TW"]));
  const [targetUrl, setTargetUrl] = useState("");

  // Build multi-select options from constants
  const locationOptions: MultiSelectOption[] = useMemo(() =>
    LOCATION_OPTIONS.map(o => ({ value: String(o.value), label: o.label })), []
  );
  const languageOptions: MultiSelectOption[] = useMemo(() =>
    LANGUAGE_OPTIONS.map(o => ({ value: o.value, label: o.label })), []
  );

  // Fetch projects for dropdown
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => {
      const r = await projectsApi.list();
      return r.data || [];
    },
  });

  const batchMutation = useMutation({
    mutationFn: async (data: {
      keywords: string[];
      project_id?: string;
      target_url?: string;
    }) => {
      // Resolve selected locations and languages (default if empty)
      const locations = locationCodes.size > 0 ? [...locationCodes] : ["2158"];
      const languages = languageCodes.size > 0 ? [...languageCodes] : ["zh-TW"];

      // If creating new project, create it first (use first selected as default)
      let projectId = data.project_id;
      if (createNewProject && projectName.trim()) {
        const projRes = await projectsApi.create({
          name: projectName.trim(),
          default_location_code: parseInt(locations[0]),
          default_language_code: languages[0],
        });
        projectId = projRes.data?.project_id;
      }

      // Submit one batch per location × language combination
      const results = [];
      for (const loc of locations) {
        for (const lang of languages) {
          const res = await serpApi.batchAnalyze({
            keywords: data.keywords,
            project_id: projectId,
            location_code: parseInt(loc),
            language_code: lang,
            target_url: data.target_url,
          });
          results.push(res.data);
        }
      }
      return { results, combos: locations.length * languages.length };
    },
    onSuccess: (data: any) => {
      const combos = data.combos;
      toast.success(combos > 1
        ? `已建立 ${combos} 組批量 SERP 分析任務（地區×語言組合）`
        : `已建立批量 SERP 分析任務`
      );
      setKeywords("");
      setProjectName("");
      setTargetUrl("");
    },
    onError: (err: Error) => {
      toast.error(`批量分析失敗: ${err.message}`);
    },
  });

  const handleSubmit = () => {
    if (!keywords.trim()) {
      toast.error("請輸入關鍵字");
      return;
    }

    if (createNewProject && !projectName.trim()) {
      toast.error("請輸入專案名稱");
      return;
    }

    if (!createNewProject && !selectedProjectId) {
      toast.error("請選擇專案或建立新專案");
      return;
    }

    const keywordList = keywords
      .split(/[\n,，]/)
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (keywordList.length === 0) {
      toast.error("請輸入至少一個關鍵字");
      return;
    }

    if (keywordList.length > 200) {
      toast.error("單次最多支援 200 個關鍵字");
      return;
    }

    batchMutation.mutate({
      keywords: keywordList,
      project_id: createNewProject ? undefined : selectedProjectId,
      target_url: targetUrl.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">批量 SERP 分析</h1>
        <p className="text-muted-foreground">一次提交多個關鍵字進行 SERP 分析，自動建立或關聯專案</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>批量分析設定</CardTitle>
          <CardDescription>輸入多個關鍵字（換行或逗號分隔），系統會依序執行分析</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Keywords input */}
          <div className="space-y-2">
            <Label>關鍵字列表（必填）</Label>
            <Textarea
              placeholder={"輸入關鍵字，每行一個或使用逗號分隔\n例如：\n端子台\n接線端子\n端子盤"}
              value={keywords}
              onChange={e => setKeywords(e.target.value)}
              rows={8}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              支援換行或逗號分隔，最多 200 個關鍵字
            </p>
          </div>

          {/* Project selection */}
          <div className="space-y-2">
            <Label>專案管理</Label>
            <div className="flex items-center gap-4">
              <Button
                variant={!createNewProject ? "default" : "outline"}
                onClick={() => setCreateNewProject(false)}
                className="flex-1"
              >
                使用現有專案
              </Button>
              <Button
                variant={createNewProject ? "default" : "outline"}
                onClick={() => setCreateNewProject(true)}
                className="flex-1"
              >
                建立新專案
              </Button>
            </div>
          </div>

          {!createNewProject ? (
            <div className="space-y-2">
              <Label>選擇專案</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="選擇專案" />
                </SelectTrigger>
                <SelectContent>
                  {projects.length === 0 ? (
                    <SelectItem value="__none__" disabled>尚無專案，請建立新專案</SelectItem>
                  ) : (
                    projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>新專案名稱（必填）</Label>
              <Input
                placeholder="輸入專案名稱"
                value={projectName}
                onChange={e => setProjectName(e.target.value)}
              />
            </div>
          )}

          {/* Target URL */}
          <div className="space-y-2">
            <Label>目標網址（可選）</Label>
            <Input
              placeholder="https://example.com"
              value={targetUrl}
              onChange={e => setTargetUrl(e.target.value)}
            />
          </div>

          {/* Location and Language (multi-select) */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>地區 Region</Label>
              <MultiSelectFilter
                label="選擇地區"
                options={locationOptions}
                selected={locationCodes}
                onChange={setLocationCodes}
              />
              {locationCodes.size > 1 && (
                <p className="text-xs text-muted-foreground">
                  已選 {locationCodes.size} 個地區，將產生多組批次
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>語言 Language</Label>
              <MultiSelectFilter
                label="選擇語言"
                options={languageOptions}
                selected={languageCodes}
                onChange={setLanguageCodes}
              />
              {languageCodes.size > 1 && (
                <p className="text-xs text-muted-foreground">
                  已選 {languageCodes.size} 個語言，將產生多組批次
                </p>
              )}
            </div>
          </div>
          {locationCodes.size > 1 && languageCodes.size > 1 && (
            <p className="text-xs text-amber-500">
              共 {locationCodes.size * languageCodes.size} 組地區×語言組合，每組獨立產生批次任務
            </p>
          )}

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={batchMutation.isPending || !keywords.trim()}
            className="w-full"
            size="lg"
          >
            {batchMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                提交中...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                開始批量分析
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Success result */}
      {batchMutation.isSuccess && (
        <Card>
          <CardHeader>
            <CardTitle>批量分析結果</CardTitle>
            <CardDescription>任務已建立，系統將依序執行分析</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span className="font-medium">批量分析已啟動</span>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <Label className="text-muted-foreground mb-2 block">注意事項</Label>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>- 批量分析會依序執行（每個請求間隔 1 秒），避免 API 超載</li>
                <li>- 批量模式預設只爬取前 20 名（高優先級）</li>
                <li>- 您可以在「專案管理」頁面查看專案詳情</li>
                <li>- 您可以在「專案匯出」頁面查看和匯出結果</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
